/**
 * Arbeitsbereich: der Eintrag der Gliederung, in dem man gerade arbeitet (Design 4.4).
 *
 * - zuletzt gewaehlt (je Browser gemerkt), sonst der eigene Eintrag (erste Zuordnung in
 *   Baumreihenfolge), sonst die oberste Ebene;
 * - Kopf zeigt den Pfad dorthin, das Band darunter „Du arbeitest in …“;
 * - neue Objekte gehoeren ihm: `lib/api` schickt ihn als Header mit, der Katalog nimmt ihn als
 *   Vorgabe, wenn ein Formular nichts anderes angibt;
 * - Listen (Katalog, Quellen, Meldungen, Ableitungen, Prognosen) zeigen ihn und alles darunter.
 *
 * Ohne Gliederung (Einzelkunde) gibt es keinen Arbeitsbereich: alles wie bisher.
 */
import { useSyncExternalStore } from 'react';

import { canWriteAt, hasStructure, organisationsState, rootOf, subtreeOf, useOrganisations, type OrganisationNode, type OrganisationsState } from './organisations.js';

const KEY = 'civiport.workspace';

let chosen: string | null = read();
let version = 0;
const listeners = new Set<() => void>();

function read(): string | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function setWorkspace(id: string): void {
  chosen = id;
  try {
    window.localStorage.setItem(KEY, id);
  } catch {
    // ohne Speicher gilt die Wahl bis zum Neuladen
  }
  version += 1;
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Aktueller Arbeitsbereich; null ohne Gliederung oder solange der Baum nicht geladen ist. */
export function workspaceOf(s: OrganisationsState, stored: string | null = chosen): OrganisationNode | null {
  if (!s.loaded || !hasStructure(s.items)) return null;
  const byId = new Map(s.items.map((o) => [o.id, o]));
  return (stored ? byId.get(stored) : undefined) ?? (s.my.home ? byId.get(s.my.home) : undefined) ?? rootOf(s.items) ?? null;
}

/** Fuer `lib/api`: Id des Arbeitsbereichs (Header), ohne React. */
export function currentWorkspaceId(): string | null {
  return workspaceOf(organisationsState())?.id ?? null;
}

export interface Workspace {
  node: OrganisationNode;
  /** Der Eintrag und alle darunter (fuer Filter). */
  subtree: Set<string>;
  canWrite: boolean;
}

export function useWorkspace(): Workspace | null {
  useSyncExternalStore(subscribe, () => version);
  const s = useOrganisations();
  const node = workspaceOf(s);
  if (!node) return null;
  return { node, subtree: new Set(subtreeOf(s.items, node.id)), canWrite: canWriteAt(s, node.id) };
}

/**
 * Gehoert ein Objekt (Eintrag `organisationId`, null = oberste Ebene) in den Arbeitsbereich?
 * Ohne Arbeitsbereich: ja.
 */
export function inWorkspace(ws: Workspace | null, organisationId: string | null | undefined, rootId?: string): boolean {
  if (!ws) return true;
  const id = organisationId ?? rootId;
  return !!id && ws.subtree.has(id);
}
